import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, openBillingPortal } from "@/lib/api";

export function BillingActions() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const returned = params.get("billing") === "done";
  const highlight = params.get("update_card") === "1";
  useEffect(() => {
    if (!returned) return;
    const hadIssue = user.payment_issue;
    api.post("/payments/sync").then((r) => {
      setUser(r.data);
      if (hadIssue && !r.data.payment_issue) toast.success("Payment received — you're all set");
      else if (r.data.payment_issue) toast.error("We still couldn't charge your card. Please check the details or try another card.");
      else toast.success("Billing details updated");
    }).catch(() => {});
    setParams({}, { replace: true });
  }, [returned, setParams, setUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPortal = async () => {
    setBusy(true);
    try { await openBillingPortal(); }
    catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };

  const family = user.role === "user" && user.family_plan;
  if (user.is_family_member || (!user.has_billing && !family)) return null;
  return (
    <div className="space-y-3 pt-2">
      {user.payment_issue && (
        <div data-testid="billing-payment-issue" className="rounded-2xl border border-[#FF3B6B]/50 bg-[#FF3B6B]/10 p-4 space-y-3">
          <p className="text-sm text-[#FF7A7A] font-semibold">
            {user.payment_locked ? "Your first payment didn't go through, so your access is paused." : "Your last payment didn't go through. You still have access while we retry."}
          </p>
          <Button data-testid="account-update-card" onClick={openPortal} disabled={busy}
            className={`w-full h-12 rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold ${highlight ? "ring-2 ring-[#FFB800] ring-offset-2 ring-offset-[#151C2C]" : ""}`}>
            <CreditCard className="h-4 w-4 mr-2" /> {busy ? "Opening secure billing…" : "Update my card"}
          </Button>
        </div>
      )}
      {user.cancel_at_period_end && <p data-testid="billing-cancel-notice" className="text-xs text-[#FFD466]">Your plan is set to cancel at the end of this billing period.</p>}
      {user.has_billing && (
        <Button data-testid="account-manage-billing" onClick={openPortal} disabled={busy} variant="outline"
          className="w-full h-12 rounded-full border-[#26334D] bg-transparent hover:bg-[#1E293B] font-semibold">
          <CreditCard className="h-4 w-4 mr-2" /> {busy ? "Opening secure billing…" : "Manage billing"}
        </Button>
      )}
      {family && (
        <Button data-testid="account-switch-profile" onClick={() => navigate("/profiles")} variant="outline"
          className="w-full h-12 rounded-full border-[#26334D] bg-transparent hover:bg-[#1E293B] font-semibold">
          <Users className="h-4 w-4 mr-2" /> {user.active_profile ? `Switch profile (${user.active_profile.name})` : "Choose family profile"}
        </Button>
      )}
    </div>
  );
}
